/**
 * The two formats that arrive as a scene rather than as a mesh.
 *
 * Both are read by a loader from three's addons and both come back as an object tree; what
 * differs is everything about how they get there, which is why the difference is confined
 * to this one function.
 *
 * **glTF** resolves through promises — buffers, then materials, then textures — so there
 * is no synchronous route to one. That single fact is what makes reading any model in this
 * application asynchronous.
 *
 * **3MF** parses in one go, but needs `DOMParser`: it is a zip of XML, and the loader uses
 * the browser's own parser to read it. That is the one thing here with no equivalent
 * outside a browser, so a 3MF cannot be read in a plain Node process.
 *
 * A 3MF is passed through `read3mf` first, which redirects faces the loader would
 * otherwise drop, reads the unit it never applies, and finds the translucency it loses.
 * See that file for why.
 *
 * Neither loader is asked to fetch anything. A file dropped into a browser brings no
 * neighbours, so a document pointing at one is refused before it gets here — see
 * `explainGltfRefusal`.
 */

import { Mesh, type Material, type Object3D } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { ThreeMFLoader } from 'three/addons/loaders/3MFLoader.js';

import { MILLIMETRES_PER_UNIT } from '../domain/gltf.js';
import { read3mf, type Translucency } from './threeMfSource.js';

/**
 * Build what is inside a container, without placing it in any scene.
 *
 * Rejects rather than returning a result: the callers are already handling a loader that
 * may throw, and a second shape of failure to unpack would buy nothing.
 */
export async function buildScene(
  buffer: ArrayBuffer,
  kind: '3mf' | 'gltf',
): Promise<Object3D> {
  if (kind === 'gltf') {
    // The empty base path is deliberate and is why external references are refused up
    // front: with nothing to resolve against, a document that wanted a neighbouring file
    // would send the loader to fetch a relative URL from wherever the page happens to be.
    const gltf = await new GLTFLoader().parseAsync(buffer, '');

    // The format states its unit rather than carrying one, and states metres. Scaling the
    // root brings the whole scene into the millimetres everything above deals in —
    // measurement included, since merging walks the world matrices.
    gltf.scene.scale.setScalar(MILLIMETRES_PER_UNIT);
    return gltf.scene;
  }

  const source = await read3mf(buffer);

  // Widened deliberately: three types this as always returning a group, and it returns
  // null when the archive cannot be opened. Taking it as it really is means the failure
  // becomes an error here rather than a null travelling on as though it were a scene.
  const group = new ThreeMFLoader().parse(source.data) as Object3D | null;
  if (group === null) throw new Error('The 3MF archive could not be opened.');

  // The file states its own unit and three reads it without applying it, so the numbers
  // arrive in whatever the file counts in. Scaling the root brings the whole scene into
  // the millimetres everything above deals in — measurement included, since merging walks
  // the world matrices.
  group.scale.setScalar(source.millimetresPerUnit);
  applyTranslucency(group, source.translucency);

  return group;
}

/**
 * Let a 3MF's surfaces be as see-through as the file says they are.
 *
 * Three ways a 3MF says so, and the loader loses all three. It reads a base material's
 * opacity and then never marks the material transparent, so the figure it worked out has
 * no effect. It discards a colour group's alpha while parsing. And it builds a textured
 * material without transparency, so an image's alpha channel is thrown away — which is
 * what made a picture with a cut-out background render as blocks of whatever lay under the
 * transparent pixels.
 *
 * Texture alpha is applied to every textured material rather than only the ones whose own
 * image carries it, because nothing survives the loader to say which is which. A material
 * marked transparent whose texture is in fact opaque draws identically; it only joins a
 * later pass to do it.
 */
function applyTranslucency(root: Object3D, translucency: Translucency): void {
  root.traverse((child) => {
    if (!isMesh(child)) return;

    const materials: readonly Material[] = Array.isArray(child.material)
      ? child.material
      : [child.material];

    for (const material of materials) {
      const textured = 'map' in material && material.map !== null;
      const coloured = 'vertexColors' in material && material.vertexColors;

      if (coloured && translucency.ofColourGroups !== undefined) {
        material.opacity = translucency.ofColourGroups;
      }

      // `depthWrite` is left as it is, which for three means on. See `translucency` in
      // `sceneManager` for why a preview is easier to read that way.
      if (material.opacity < 1 || (textured && translucency.inTextures)) {
        material.transparent = true;
      }
    }
  });
}

/**
 * Whether this is a mesh, said in a way that keeps its material's type.
 *
 * `instanceof Mesh` on its own narrows to a mesh whose type arguments are unknown, which
 * makes everything reached through it untyped.
 */
function isMesh(child: Object3D): child is Mesh {
  return child instanceof Mesh;
}
